import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RegisterSchema } from '@harbor/shared';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../state/auth.js';
import { Alert, Button, Card, Field, TextInput } from '../components/ui.js';

export function Register() {
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFailure(null);

    const parsed = RegisterSchema.safeParse({ name, email, password });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.');
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      await signUp(parsed.data);
      navigate('/', { replace: true });
    } catch (caught) {
      setFailure(
        caught instanceof ApiError ? caught.problem.detail : 'Could not create your account.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  // A cheap, honest strength signal. It counts characters, not character
  // classes — length is the only property that reliably correlates with
  // resistance to offline cracking.
  const strength = Math.min(4, Math.floor(password.length / 4));
  const strengthLabels = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'];

  return (
    <div className="mx-auto flex max-w-md flex-col justify-center px-4 py-16 sm:px-6">
      <div className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Create an account</h1>
        <p className="mt-1.5 text-sm text-muted">
          Order history, saved sessions, and a basket that follows you between devices.
        </p>
      </div>

      {failure && (
        <div className="mt-6">
          <Alert tone="danger" title="Could not create your account" onDismiss={() => setFailure(null)}>
            {failure}
          </Alert>
        </div>
      )}

      <Card className="mt-6 p-6">
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Field label="Name" htmlFor="name" error={errors['name']}>
            <TextInput
              id="name"
              autoComplete="name"
              autoFocus
              required
              value={name}
              invalid={Boolean(errors['name'])}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field label="Email" htmlFor="email" error={errors['email']}>
            <TextInput
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              invalid={Boolean(errors['email'])}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            error={errors['password']}
            hint="At least 12 characters. Length matters far more than symbols."
          >
            <TextInput
              id="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              invalid={Boolean(errors['password'])}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          {password.length > 0 && (
            <div>
              <div className="flex gap-1" aria-hidden="true">
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={[
                      'h-1 flex-1 rounded-full transition-colors',
                      index < strength
                        ? strength <= 1
                          ? 'bg-danger'
                          : strength === 2
                            ? 'bg-warning'
                            : 'bg-positive'
                        : 'bg-sunken',
                    ].join(' ')}
                  />
                ))}
              </div>
              <p className="mt-1.5 text-xs text-subtle" aria-live="polite">
                {strengthLabels[strength]}
              </p>
            </div>
          )}

          <Button type="submit" fullWidth size="lg" loading={submitting}>
            Create account
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-muted">
          Already have one?{' '}
          <Link to="/login" className="font-medium text-accent hover:underline">
            Sign in
          </Link>
        </p>
      </Card>
    </div>
  );
}
